import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Sidebar from '@/components/Sidebar';
import PWAInstallBanner from '@/components/PWAInstallBanner';
import { SubscriptionBanner } from '@/components/SubscriptionBanner';
import { Loader2, Shield, X } from 'lucide-react';

const AppLayout = ({ children }) => {
  const { isAuthenticated, loading, user, subscriptionStatus } = useAuth();
  const [supportSession, setSupportSession] = useState(null);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    // Check if this is a support session (admin "Login As" mode)
    const session = localStorage.getItem('ihostpms_support_session');
    if (session) {
      try {
        setSupportSession(JSON.parse(session));
      } catch (e) {
        console.error('Failed to parse support session');
      }
    }
  }, []);

  const handleExitSupportMode = () => {
    // Clear tenant token and switch back to admin
    localStorage.removeItem('ihostpms_token');
    localStorage.removeItem('ihostpms_user');
    localStorage.removeItem('ihostpms_support_session');
    window.location.href = '/admin';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-airbnb-light-gray">
        <Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect pending tenants to approval page
  if (subscriptionStatus && subscriptionStatus.status === 'pending' && !subscriptionStatus.is_super_admin) {
    return <Navigate to="/pending-approval" replace />;
  }

  return (
    <div className="min-h-screen bg-airbnb-light-gray">
      {/* Support Mode Warning Banner */}
      {supportSession && showBanner && (
        <div 
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white py-2 px-4 shadow-lg"
          data-testid="support-mode-banner"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5" />
              <span className="font-medium">
                Support Mode: You are viewing <strong>{supportSession.tenant?.name || 'tenant'}</strong>'s account
              </span>
              <span className="text-amber-200 text-sm hidden sm:inline">
                • All actions are logged for audit purposes
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleExitSupportMode}
                className="bg-amber-600 hover:bg-amber-700 px-3 py-1 rounded-lg text-sm font-medium transition-colors"
                data-testid="exit-support-mode-btn"
              >
                Exit Support Mode
              </button>
              <button 
                onClick={() => setShowBanner(false)}
                className="p-1 hover:bg-amber-600 rounded transition-colors"
                title="Hide banner (session still active)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Sidebar />
      <main 
        className={`lg:ml-64 min-h-screen pb-16 lg:pb-0 ${supportSession && showBanner ? 'pt-12' : ''}`} 
        data-testid="main-content"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 ${supportSession && showBanner ? 'pt-4' : 'pt-14'} lg:pt-6`}>
          <SubscriptionBanner />
          {children}
        </div>
      </main>
      
      {/* PWA Install Banner */}
      <PWAInstallBanner />
    </div>
  );
};

export default AppLayout;
